import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { 
  Users, 
  Search,
  LogOut,
  Home,
  Mail,
  Phone,
  CheckCircle,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/layout/AdminLayout';
import { ToolAccessRequests } from '../../components/admin/ToolAccessPanel';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const onboardingStages = [
  { value: 'new', label: 'New', color: 'bg-[#FFF7ED] text-[#F97316]' },
  { value: 'welcome_sent', label: 'Welcome Sent', color: 'bg-[#EFF6FF] text-[#1D4ED8]' },
  { value: 'intake_pending', label: 'Intake Pending', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'intake_submitted', label: 'Intake Submitted', color: 'bg-purple-100 text-purple-700' },
  { value: 'plan_ready', label: 'Plan Ready', color: 'bg-blue-100 text-blue-700' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' }
];

export default function AdminClients() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          client.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || client.onboarding_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusInfo = (status) => {
    return onboardingStages.find(s => s.value === status) || onboardingStages[0];
  };

  const getChecklistProgress = (checklist) => {
    if (!checklist) return 0;
    const items = Object.values(checklist);
    const completed = items.filter(Boolean).length;
    return Math.round((completed / items.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminLayout title="Clients"><div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="heading-2 text-2xl text-[#0A0F1C]" data-testid="clients-title">
            Clients ({filteredClients.length})
          </h1>
        </div>
        <ToolAccessRequests />

        {/* Filters */}
        <div className="bg-white p-4 border border-[#E2E8F0] rounded-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-grow relative">
              <Input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
                data-testid="clients-search"
              />
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input w-full md:w-48"
              data-testid="clients-status-filter"
            >
              <option value="">All Status</option>
              {onboardingStages.map(stage => (
                <option key={stage.value} value={stage.value}>{stage.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Clients Grid */}
        {filteredClients.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client, index) => {
              const statusInfo = getStatusInfo(client.onboarding_status);
              const progress = getChecklistProgress(client.onboarding_checklist);
              
              return (
                <Link
                  key={client.client_id || index}
                  to={`/admin/clients/${client.client_id}`}
                  className="bg-white p-6 border border-[#E2E8F0] rounded-lg hover:shadow-lg hover:-translate-y-1 transition-all"
                  data-testid={`client-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-[#0A0F1C]">{client.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-[#475569]">
                      <Mail size={14} />
                      {client.email}
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-2 text-sm text-[#475569]">
                        <Phone size={14} />
                        {client.phone}
                      </div>
                    )}
                    {client.service_type && (
                      <div className="text-sm text-[#475569]">
                        <span className="font-medium">Service:</span> {client.service_type}
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[#475569]">Onboarding Progress</span>
                      <span className="font-medium text-[#0A0F1C]">{progress}%</span>
                    </div>
                    <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#1D4ED8] transition-all"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#94A3B8]">
                      {new Date(client.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-[#1D4ED8] font-medium flex items-center gap-1">
                      View Details <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="bg-white p-8 border border-[#E2E8F0] rounded-lg text-center">
            <Users size={48} className="text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#475569]">No clients found</p>
            <p className="text-sm text-[#94A3B8] mt-2">Convert leads to clients to see them here</p>
          </div>
        )}
      </div></AdminLayout>);}
